/**
 * PieceView — Hono-backed piece detail.
 *
 * Renamed from ArticleView in S-086 / FEAT-006. The production unit is
 * Piece (see project_piece_naming memory); the route moved from
 * /article/:id to /piece/:id and a redirect from the legacy path is
 * registered in App.tsx so old bookmarks still resolve.
 *
 * S-072 / FEAT-006 (Phase 9). Replaces the Directus-backed view
 * end-to-end:
 *
 *   - GET /pieces/:id                  — the piece row
 *   - GET /pieces/:id/drafts           — draft versions
 *   - GET /pieces/:id/reports          — pipeline reports
 *   - GET /pieces/:id/system-docs      — research artifacts
 *   - GET /pieces/:id/images           — generated images
 *   - GET /pieces/:id/run-status       — pipeline run state (polled
 *     while a run is in flight; stops on terminal status)
 *   - POST /pieces/:id/run             — admin-only pipeline trigger
 *     (gracefully surfaces 503 from the broken pg-boss queue)
 *
 * The supporting components (PieceSideColumn, panels, PieceDraftSection,
 * DecisionBar, PublicationDateEdit, LeftRail) consume snake_case
 * `PieceDetail` / `PieceDraft` / `PieceReport` / `PieceSystemDoc` /
 * `PieceImage` shapes — we adapt at the PieceView edge via
 * `@/lib/piece-adapter`. This keeps the blast radius surgical: the
 * data source flips, the UX shape does not.
 *
 * Run Pipeline button:
 *   - Admin and Editor (`useRole().canEdit`, FEAT-108 / S-438).
 *   - POST /pieces/:id/run; the route returns 202 on success, 503 when
 *     pg-boss is unavailable, 409 if a run is already in flight.
 *   - On success, start polling /pieces/:id/run-status every 5s. Stop
 *     when phase reaches `done` or `error`.
 *   - Surfaces a friendly error for the known pg-boss-broken state so
 *     the page doesn't crash.
 *
 * Workspace switch protection: when the user flips workspace via
 * useActiveWorkspace, the piece may no longer be visible (RLS). If
 * /pieces/:id returns 404 mid-view we redirect to /kanban so the
 * user lands somewhere sensible instead of staring at a blank page.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiQuery } from "@/lib/api";
import { describeRunError, describeRunErrorMessage, visibleRunError } from "@/lib/run-errors";
import type { OpenDoc } from "@/types/piece";
import { PieceSideColumn } from "@/components/PieceSideColumn";
import { VoiceReportDoc } from "@/components/VoiceReportDoc";
import { PieceSummaryDoc } from "@/components/PieceSummaryDoc";
import { PublicationAttention } from "@/components/panels/PublishedPanel";
import { PieceAmplificationsDoc } from "@/components/PieceAmplificationsDoc";

import { PieceBody } from "@/components/PieceBody";
import { DecisionBar } from "@/components/DecisionBar";
import { NewsletterDeepDiveSelector } from "@/components/NewsletterDeepDiveSelector";
import { PieceDraftSection } from "@/components/PieceDraftSection";
import { PublicationDateEdit } from "@/components/PublicationDateEdit";
import { r2AssetUrl } from "@/lib/asset-url";
import { Icon } from "@/components/Icon";
import { pieceStateLabel } from "@/lib/ui-state";
import { ageString, formatContentType } from "@/lib/format";
import { formatDate } from "@/lib/date-format";
import { useDateFormat } from "@/lib/useDateFormat";
import { isFictionArticle, pipelinePhasesFor } from "@shared/fiction-phases";
import type { PieceDraft, PieceRunEvent } from "@/types/piece";
import {
  adaptDraft,
  adaptImage,
  adaptPiece,
  adaptReport,
  adaptSystemDoc,
  type HonoListResponse,
  type HonoPiece,
  type HonoPieceDraft,
  type HonoPieceImage,
  type HonoPieceReport,
  type HonoPieceSystemDoc,
} from "@/lib/piece-adapter";
import { useAuth } from "@clerk/clerk-react";
import { useActiveWorkspace } from "@/lib/useActiveWorkspace";
import { useRole } from "@/lib/useRole";
import { pieceAccessAction } from "@/lib/piece-access";
import { useWorkspaceContext } from "@/lib/workspace-context";

// ---------- Publication shape (FEAT-015 — language gate + variant) ----------

interface HonoPublication {
  id: number;
  defaultLanguage?: string;
  default_language?: string;
  spanishCountry?: string | null;
  spanish_country?: string | null;
}

// FEAT-015 — Spanish country options in the spec's confirmed order, plus a
// label lookup for rendering the effective value.
const SPANISH_COUNTRY_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: "MX", label: "Mexico" },
  { code: "US", label: "United States (Spanish)" },
  { code: "CO", label: "Colombia" },
  { code: "AR", label: "Argentina" },
  { code: "PE", label: "Peru" },
  { code: "VE", label: "Venezuela" },
  { code: "ES", label: "Spain" },
  { code: "CL", label: "Chile" },
  { code: "GT", label: "Guatemala" },
  { code: "EC", label: "Ecuador" },
  { code: "CU", label: "Cuba" },
  { code: "BO", label: "Bolivia" },
  { code: "DO", label: "Dominican Republic" },
  { code: "HN", label: "Honduras" },
  { code: "PY", label: "Paraguay" },
  { code: "SV", label: "El Salvador" },
  { code: "NI", label: "Nicaragua" },
  { code: "CR", label: "Costa Rica" },
  { code: "PA", label: "Panama" },
  { code: "PR", label: "Puerto Rico" },
  { code: "UY", label: "Uruguay" },
  { code: "neutral", label: "Neutral Spanish" },
];

const SPANISH_COUNTRY_LABELS: Record<string, string> = Object.fromEntries(
  SPANISH_COUNTRY_OPTIONS.map((o) => [o.code, o.label])
);

function spanishVariantLabel(code: string | null): string {
  if (!code) return "Not set";
  return SPANISH_COUNTRY_LABELS[code] ?? code;
}

// ---------- Run status response shape ----------

interface RunStatusResponse {
  jobId: string | null;
  phase: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
}

interface RunEnqueueResponse {
  jobId: string;
  pipelineType: "content" | "fiction" | "newsletter";
  message: string;
}

// ---------- Helpers ----------

function isTerminalPhase(phase: string | null | undefined): boolean {
  return phase === "done" || phase === "error" || phase === null;
}

// FEAT-033 / bug fix (2026-07-12) — "awaiting_review" is the generic ui_state
// for EVERY configured review-stage checkpoint (FEAT-025), not just the
// initial Deep Dive candidate gate. A newsletter piece legitimately returns
// to "awaiting_review" after Auditor/Editor/Art Director if any of those
// phases are in the publication's reviewStages (the default config includes
// all seven) — the selector must not reappear once a selection already
// exists. This mirrors the check NewsletterResumeOrchestrator itself makes
// server-side (apps/api/src/pipeline/orchestrators/newsletter-resume.ts) so
// client and server agree on the same signal.
function hasDeepDiveSelections(editorialDirection: string | null): boolean {
  if (!editorialDirection) return false;
  try {
    const parsed = JSON.parse(editorialDirection) as { deepDiveSelections?: unknown };
    return Array.isArray(parsed.deepDiveSelections) && parsed.deepDiveSelections.length > 0;
  } catch {
    return false;
  }
}

export function PieceView() {
  const dateFormat = useDateFormat();
  const params = useParams();
  const id = params.id ? Number(params.id) : null;
  // FEAT-087 — the main window shows the piece: the current draft, an older
  // version, or an agent document. Everything else the pipeline knows is a
  // section of the side column, which is the screen's information surface.
  // Nothing overlays the document.
  const [openTrailGroup, setOpenTrailGroup] = useState<string | null>(null);
  const [openDoc, setOpenDoc] = useState<OpenDoc>(null);
  const [previewDraftId, setPreviewDraftId] = useState<number | null>(null);

  /** Reading a document and previewing an old version are exclusive. */
  const showDoc = useCallback((doc: OpenDoc) => {
    setOpenDoc(doc);
    if (doc) setPreviewDraftId(null);
  }, []);
  const selectDraft = useCallback((id: number | null) => {
    setPreviewDraftId(id);
    if (id != null) setOpenDoc(null);
  }, []);
  const closeReader = useCallback(() => {
    setOpenDoc(null);
    setPreviewDraftId(null);
  }, []);

  // Escape returns the main window to the current draft, from a document or
  // an older version alike. The screen offered no keyboard escape before.
  useEffect(() => {
    if (!openDoc && previewDraftId == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeReader();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDoc, previewDraftId, closeReader]);

  // Active workspace for the left-rail queue. Reading via the JWT
  // claim would be ideal, but the queue need agencyclient_id to query
  // /pieces; we re-derive from the active workspace state.
  const piece = useApiQuery<HonoPiece>(["piece", id], `/pieces/${id}`, {
    enabled: id != null,
    refetchInterval: (query) => {
      const phase = (query.state.data as HonoPiece | undefined)?.run_phase;
      if (!phase || phase === "done" || phase === "error") return false;
      return 5000;
    },
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 1;
    },
  });

  const adaptedPiece = useMemo(
    () => (piece.data ? adaptPiece(piece.data) : null),
    [piece.data]
  );

  // ---- BUG-071 recovery -------------------------------------------------
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const [claimAgencyclientId, setClaimAgencyclientId] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getToken({ template: "ccm-default" })
      .then((t) => {
        if (cancelled || !t) return;
        const raw = t.split(".")[1];
        if (!raw) return;
        const json = JSON.parse(
          atob(raw.replace(/-/g, "+").replace(/_/g, "/"))
        ) as { agencyclient_id?: number };
        setClaimAgencyclientId(json.agencyclient_id ?? null);
      })
      .catch(() => {
        // A token we cannot read just means the "already on it" shortcut
        // does not fire; the switch path still works.
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const pieceNotFound =
    piece.error instanceof ApiError && piece.error.status === 404;

  // Which of the user's workspaces holds this piece. Membership-scoped and
  // id-only: a piece in a workspace you are not in answers 404, the same as
  // one that does not exist.
  const locate = useApiQuery<{ agencyclient_id: number }>(
    ["piece-locate", id],
    `/overview/pieces/${id}/locate`,
    { enabled: pieceNotFound && id != null, retry: false }
  );

  const { activate } = useActiveWorkspace();
  const [switching, setSwitching] = useState(false);
  const attemptedRef = useRef<number | null>(null);

  const access = pieceAccessAction({
    notFound: pieceNotFound,
    locatedIn: locate.isLoading ? undefined : (locate.data?.agencyclient_id ?? null),
    activeAgencyclientId: claimAgencyclientId,
    alreadyAttempted: attemptedRef.current === id,
  });

  // BUG-137: while this piece is open its workspace is the one in force, so
  // the sidebar cannot switch the claim away and empty the piece's sections.
  const { setPinnedId } = useWorkspaceContext();
  // Pinned once the piece has loaded, so the BUG-071 switch below stays the
  // only activation while a piece is still being located.
  const pieceWorkspaceId = piece.data?.agencyclient_id ?? null;
  useEffect(() => {
    if (pieceWorkspaceId != null) setPinnedId(pieceWorkspaceId);
  }, [pieceWorkspaceId, setPinnedId]);
  useEffect(() => () => setPinnedId(null), [setPinnedId]);

  useEffect(() => {
    if (access.kind !== "switch" || switching) return;
    attemptedRef.current = id;
    setSwitching(true);
    void activate(access.agencyclientId)
      .then(() => qc.invalidateQueries({ queryKey: ["piece", id] }))
      .finally(() => setSwitching(false));
  }, [access, switching, activate, id, qc]);

  // FEAT-015 — the piece's publication supplies the language gate + default
  // Spanish variant for the metadata row. Loaded once the piece is known.
  const publication = useApiQuery<HonoPublication>(
    ["publication", piece.data?.publication_id ?? 0],
    `/publications/${piece.data?.publication_id ?? 0}`,
    { enabled: piece.data?.publication_id != null }
  );

  // Queue (left rail): list of pieces in the same workspace as this one.
  // Wait for the piece to load so we know the agencyclient id; otherwise
  // we don't have a scope filter.

  const drafts = useApiQuery<HonoListResponse<HonoPieceDraft>>(
    ["piece-drafts", id],
    `/pieces/${id}/drafts?include_content=false`,
    { enabled: id != null }
  );

  const reports = useApiQuery<HonoListResponse<HonoPieceReport>>(
    ["piece-reports", id],
    `/pieces/${id}/reports?include_content=false`,
    { enabled: id != null }
  );

  const systemDocs = useApiQuery<HonoListResponse<HonoPieceSystemDoc>>(
    ["piece-system-docs", id],
    `/pieces/${id}/system-docs?include_content=false`,
    { enabled: id != null }
  );

  const images = useApiQuery<HonoListResponse<HonoPieceImage>>(
    ["piece-images", id],
    `/pieces/${id}/images`,
    { enabled: id != null }
  );

  const adaptedDrafts = useMemo(
    () => (drafts.data?.data ?? []).map(adaptDraft),
    [drafts.data]
  );
  const adaptedReports = useMemo(
    () => (reports.data?.data ?? []).map(adaptReport),
    [reports.data]
  );
  const adaptedSystemDocs = useMemo(
    () => (systemDocs.data?.data ?? []).map(adaptSystemDoc),
    [systemDocs.data]
  );
  const adaptedImages = useMemo(
    () => (images.data?.data ?? []).map(adaptImage),
    [images.data]
  );

  const latestDraft = useMemo<PieceDraft | null>(() => {
    if (!adaptedDrafts.length) return null;
    const PRIORITY = [
      "seo_draft",
      "corrected_draft",
      "draft",
      "outline",
      "abstract",
    ];
    for (const pageType of PRIORITY) {
      const found = adaptedDrafts
        .filter((d) => d.page_type === pageType)
        .sort(
          (a, b) =>
            (b.version ?? 0) - (a.version ?? 0) ||
            b.date_created.localeCompare(a.date_created)
        );
      if (found.length) return found[0];
    }
    return adaptedDrafts[0] ?? null;
  }, [adaptedDrafts]);

  // The drafts list is metadata-only now, so the draft actually being shown
  // fetches its own content — one draft instead of every version. Piece 86
  // went from 52 KB of drafts to a single ~13 KB read.
  const shownDraftId = previewDraftId ?? latestDraft?.id ?? null;
  // The document being read fetches its own text, since the lists no longer
  // carry it. MUST stay above the early returns below — a hook that some
  // renders skip is React error #310, which is exactly how this shipped
  // broken the first time.
  const readingDoc =
    openDoc && (openDoc.kind === "report" || openDoc.kind === "system_doc")
      ? openDoc
      : null;
  const openDocQuery = useApiQuery<{ content: string | null }>(
    ["piece-doc", id, readingDoc?.kind, readingDoc?.id],
    readingDoc
      ? `/pieces/${id}/${readingDoc.kind === "report" ? "reports" : "system-docs"}/${readingDoc.id}`
      : `/pieces/${id}/reports/0`,
    { enabled: readingDoc != null, staleTime: 5 * 60_000 }
  );

  const shownDraftQuery = useApiQuery<{ content: string | null }>(
    ["piece-draft-content", id, shownDraftId],
    `/pieces/${id}/drafts/${shownDraftId ?? 0}`,
    { enabled: shownDraftId != null, staleTime: 5 * 60_000 }
  );
  const shownDraftContent = shownDraftQuery.data?.content ?? null;

  const previewDraft = useMemo(
    () =>
      previewDraftId
        ? (() => {
            const meta = adaptedDrafts.find((d) => d.id === previewDraftId);
            return meta ? { ...meta, content: shownDraftContent } : null;
          })()
        : null,
    [previewDraftId, adaptedDrafts, shownDraftContent]
  );

  /** The current draft, with the content fetched for it. */
  const latestDraftWithContent = useMemo<PieceDraft | null>(
    () =>
      latestDraft
        ? { ...latestDraft, content: previewDraftId ? latestDraft.content : shownDraftContent }
        : null,
    [latestDraft, shownDraftContent, previewDraftId]
  );

  const isFiction = adaptedPiece
    ? isFictionArticle({
        content_type: adaptedPiece.content_type,
        generation_phases: adaptedPiece.generation_phases,
      })
    : false;

  // FEAT-033 — newsletter editorial gate: parse deep_dive_candidates for selector.
  // This fires on awaiting_review (the initial editorial gate before deep-dive
  // creation), NOT on awaiting_approval (the final publish gate after all AI
  // phases complete). Confusing the two silently disables the Approve button.
  //
  // ui_state === "awaiting_review" alone isn't enough — see hasDeepDiveSelections
  // above. Once a selection is recorded, generation_phases also moves off
  // "phase_3_written" (the only value it holds before a selection exists), so
  // that's checked too as cheap defense-in-depth. "phase_3_written" is a
  // newsletter-only phase value not in the shared GenerationPhase union
  // (regular content's phase 3 is "phase_3_draft_generated") — compare as a
  // plain string rather than widening that shared type for one check.
  const isNewsletterGated =
    adaptedPiece?.content_type === "newsletter" &&
    adaptedPiece?.ui_state === "awaiting_review" &&
    (adaptedPiece?.generation_phases as string | null) === "phase_3_written" &&
    !hasDeepDiveSelections(adaptedPiece?.editorial_direction ?? null);

  // BUG — the eight candidates stopped appearing on 2026-09-13.
  //
  // eaaf58f (2026-09-06, "list endpoints can skip the document text") added
  // `?include_content=false` to the system-docs list above to save egress. This
  // block reads `doc.content` from that same list, so it has been empty ever
  // since: the list is asked not to send the text, then required to have it.
  //
  // A week passed before anything tripped it. The 09-06 newsletter was curated
  // at 01:46 and its candidates were chosen before that commit landed, and the
  // newsletter runs weekly — so the next edition was the first to see it.
  //
  // Fixed by fetching the one document on demand, the same way `readingDoc`
  // does, rather than by reverting the egress saving. The candidates doc is
  // ~10KB and only this gate needs it.
  const candidatesDocId = useMemo(
    () =>
      isNewsletterGated
        ? (adaptedSystemDocs.find((d) => d.page_type === "deep_dive_candidates")?.id ?? null)
        : null,
    [isNewsletterGated, adaptedSystemDocs]
  );
  const candidatesQuery = useApiQuery<{ content: string | null }>(
    ["piece-doc", id, "system_doc", candidatesDocId],
    candidatesDocId != null
      ? `/pieces/${id}/system-docs/${candidatesDocId}`
      : `/pieces/${id}/reports/0`,
    { enabled: candidatesDocId != null, staleTime: 5 * 60_000 }
  );

  const newsletterCandidates = useMemo(() => {
    if (!isNewsletterGated) return [];
    const content = candidatesQuery.data?.content;
    if (!content) return [];
    try {
      return JSON.parse(content) as Array<{
        rank: number;
        title: string;
        url: string;
        source_name: string;
        relevance_reason: string;
        takeaways: string[];
        content_excerpt: string;
      }>;
    } catch {
      return [];
    }
  }, [isNewsletterGated, candidatesQuery.data]);

  const featuredImage = useMemo(() => {
    const all = adaptedImages;
    const featuredLandscape = all.find(
      (i) => i.selected_featured && i.format === "landscape" && i.image
    );
    if (featuredLandscape) return featuredLandscape;
    const anyLandscape = all.find((i) => i.format === "landscape" && i.image);
    if (anyLandscape) return anyLandscape;
    return all.find((i) => i.selected_featured && i.image) ?? null;
  }, [adaptedImages]);

  const phases = adaptedPiece
    ? pipelinePhasesFor({
        content_type: adaptedPiece.content_type,
        generation_phases: adaptedPiece.generation_phases,
      })
    : [];

  if (piece.isLoading) {
    return (
      <main className="main">
        <div className="state-loading">Loading piece…</div>
      </main>
    );
  }

  // BUG-071 — a 404 here almost always means the piece is in one of your
  // other workspaces: RLS matches app.agencyclient_id exactly, and this is
  // the one view that cannot sync the claim ahead of time, because it does
  // not know the workspace until the piece loads. Recover by switching to
  // the workspace that holds it, once. Never redirect: `replace` discarded
  // the URL, so a shared link vanished with no explanation.
  if (pieceNotFound) {
    if (locate.isLoading || switching) {
      return (
        <main className="main">
          <div className="state-loading">Opening this piece's workspace…</div>
        </main>
      );
    }
    if (access.kind === "unavailable") {
      return (
        <main className="main">
          <div className="state-error">{access.reason}</div>
        </main>
      );
    }
    return (
      <main className="main">
        <div className="state-loading">Opening this piece's workspace…</div>
      </main>
    );
  }

  if (piece.error || !adaptedPiece) {
    return (
      <main className="main">
        <div className="state-error">
          {piece.error
            ? `Could not load piece: ${(piece.error as Error).message}`
            : "Piece not found."}
        </div>
      </main>
    );
  }

  const p = adaptedPiece;
  const stageLabel = pieceStateLabel(p);
  const dueLabel = p.publication_date ? formatDate(p.publication_date, dateFormat, { withYear: true }) : null;

  // FEAT-015 — show the Spanish variant row only for es publications.
  const pubLanguage =
    publication.data?.defaultLanguage ?? publication.data?.default_language ?? null;
  const pubSpanishDefault =
    publication.data?.spanishCountry ?? publication.data?.spanish_country ?? null;
  const showSpanishVariant = pubLanguage === "es";

  const openContent = (() => {
    if (!readingDoc) return null;
    const meta =
      readingDoc.kind === "report"
        ? adaptedReports.find((x) => x.id === readingDoc.id)
        : adaptedSystemDocs.find((x) => x.id === readingDoc.id);
    if (!meta) return null;
    return {
      title: meta.title,
      type: meta.page_type,
      content: openDocQuery.data?.content ?? null,
      loading: openDocQuery.isLoading,
    };
  })();

  const openImage =
    openDoc?.kind === "image"
      ? adaptedImages.find((i) => i.id === openDoc.id) ?? null
      : null;

  return (
    <>
      <main className="main">
        <div className="article-wrap">
          <header className="art-header">
            <div className="art-header-row1">
              <div className="art-header-meta">
                <span className="meta-pill mono">#{p.id}</span>
                <span className="meta-dot" />
                <span>{formatContentType(p.content_type)}</span>
                {p.tone && (
                  <>
                    <span className="meta-dot" />
                    <span>{p.tone}</span>
                  </>
                )}
                <span className="meta-dot" />
                <span>
                  {latestDraft
                    ? `Draft v${latestDraft.version ?? 1} · ${ageString(latestDraft.date_created)}`
                    : `${ageString(p.date_updated || p.date_created)}`}
                </span>
                {showSpanishVariant && (
                  <>
                    <span className="meta-dot" />
                    <SpanishVariantEdit
                      pieceId={p.id}
                      override={p.spanish_country_override ?? null}
                      publicationDefault={pubSpanishDefault}
                    />
                  </>
                )}
                <span className="meta-dot" />
                <CategoryEdit pieceId={p.id} category={p.category ?? null} />
              </div>
              <div className="art-header-stage">
                <span className="stage-pulse" />
                <span className="stage-label">Current stage</span>
                <span className="stage-name">{stageLabel}</span>
              </div>
              <div className="publish-info">
                <PublicationDateEdit
                  pieceId={p.id}
                  publicationDate={p.publication_date}
                  fallbackLabel={dueLabel || stageLabel}
                />
              </div>
            </div>
            <DecisionBar
              piece={p}
              suppressNextStep={isNewsletterGated}
              draftHtml={latestDraftWithContent?.content ?? null}
            />
            <RunPipelineRow
              pieceId={p.id}
              agencyclientId={piece.data?.agencyclient_id ?? 0}
              runPhaseFromRow={piece.data?.run_phase ?? null}
              runErrorFromRow={piece.data?.run_error ?? null}
            />
          </header>

          <PublicationAttention pieceId={p.id} />

          {openDoc?.kind === "voice" ? (
            <article className="article report-view">
              <button
                className="report-close"
                onClick={closeReader}
                aria-label="Close assessment"
              >
                ✕
              </button>
              <div className="article-meta">
                <span className="meta-pill mono">VOICE &amp; STYLE</span>
              </div>
              <h1 className="article-title">{p.title || p.slug}</h1>
              <VoiceReportDoc
                piece={p}
                edited={adaptedDrafts.some((d) => d.page_type === "corrected_draft")}
              />
            </article>
          ) : openDoc?.kind === "summary" ? (
            <article className="article report-view">
              <button
                className="report-close"
                onClick={closeReader}
                aria-label="Close summary"
              >
                ✕
              </button>
              <div className="article-meta">
                <span className="meta-pill mono">SUMMARY</span>
              </div>
              <h1 className="article-title">{p.title || p.slug}</h1>
              <PieceSummaryDoc
                piece={p}
                latestDraft={latestDraftWithContent}
                reports={adaptedReports}
              />
            </article>
          ) : openDoc?.kind === "amplifications" ? (
            <article className="article report-view">
              <button
                className="report-close"
                onClick={closeReader}
                aria-label="Close amplifications"
              >
                ✕
              </button>
              <div className="article-meta">
                <span className="meta-pill mono">AMPLIFICATIONS</span>
              </div>
              <h1 className="article-title">{p.title || p.slug}</h1>
              <PieceAmplificationsDoc
                pieceId={p.id}
                agencyclientId={piece.data?.agencyclient_id ?? 0}
              />
            </article>
          ) : openImage ? (
            <article className="article report-view">
              <button
                className="report-close"
                onClick={closeReader}
                aria-label="Close image"
              >
                ✕
              </button>
              <div className="article-meta">
                <span className="meta-pill mono">IMAGE</span>
                {openImage.style && <span>{openImage.style}</span>}
                {openImage.format && <span>{openImage.format}</span>}
              </div>
              <h1 className="article-title">
                {openImage.name || formatContentType(openImage.style)}
              </h1>
              {openImage.image ? (
                <figure className="article-hero">
                  <img
                    src={r2AssetUrl(openImage.image, { width: 1600 })}
                    alt={openImage.name || p.title || ""}
                  />
                </figure>
              ) : (
                <p className="pside-empty">This image has no file attached.</p>
              )}
              {openImage.prompt && (
                <>
                  <h2 className="psummary-h">Prompt</h2>
                  <p className="psummary-brief">{openImage.prompt}</p>
                </>
              )}
            </article>
          ) : openContent ? (
            <article className="article report-view">
              <button
                className="report-close"
                onClick={closeReader}
                aria-label="Close document"
              >
                ✕
              </button>
              <div className="article-meta">
                <span className="meta-pill mono">
                  {openDoc?.kind === "report" ? "REPORT" : "DOC"} · {openContent.type}
                </span>
              </div>
              <h1 className="article-title">
                {openContent.title || formatContentType(openContent.type)}
              </h1>
              {openContent.loading ? (
                <div className="state-loading">Loading this document…</div>
              ) : (
                <PieceBody markdown={openContent.content} />
              )}
            </article>
          ) : (
            <article className="article">
              <div className="article-title-row">
                <h1 className="article-title">{p.title || p.slug}</h1>
              </div>
              {p.abstract && <p className="article-dek">{p.abstract}</p>}
              <div className="article-byline">
                <span>{isFiction ? "Fiction · Generated by nagareru" : "Generated by nagareru"}</span>
                {p.target_word_count && (
                  <>
                    <span className="meta-dot" />
                    <span>{p.target_word_count.toLocaleString()} word target</span>
                  </>
                )}
              </div>
              {featuredImage?.image && (
                <figure className="article-hero">
                  <img
                    src={r2AssetUrl(featuredImage.image, {
                      width: 1280,
                      fit: "cover",
                    })}
                    alt={featuredImage.name || p.title || ""}
                    loading="lazy"
                  />
                  {featuredImage.name && (
                    <figcaption>{featuredImage.name}</figcaption>
                  )}
                </figure>
              )}
              {isNewsletterGated && newsletterCandidates.length > 0 ? (
                <NewsletterDeepDiveSelector
                  pieceId={p.id}
                  generationPhases={p.generation_phases}
                  candidates={newsletterCandidates}
                />
              ) : previewDraft ? (
                <>
                  <div
                    className="version-preview-banner"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "8px 0 16px",
                      fontSize: 13,
                      color: "var(--muted-fg)",
                      borderBottom: "1px solid var(--rule)",
                      marginBottom: 24,
                    }}
                  >
                    <span className="pill pill-muted">
                      Preview — v{previewDraft.version ?? "?"} {previewDraft.page_type}
                    </span>
                    <button
                      type="button"
                      className="meta-link-btn"
                      onClick={() => setPreviewDraftId(null)}
                      style={{
                        fontSize: 13,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        color: "var(--accent, #2563eb)",
                      }}
                    >
                      Back to current
                    </button>
                  </div>
                  <PieceBody markdown={previewDraft.content} stripTitle />
                </>
              ) : (
                <PieceDraftSection
                  pieceId={p.id}
                  pieceTitle={p.title}
                  status={p.status}
                  latestDraft={latestDraftWithContent}
                />
              )}
              <footer className="article-foot">
                <span className="mono">END · #{p.id}</span>
              </footer>
            </article>
          )}
        </div>
      </main>
      <PieceSideColumn
        piece={p}
        reports={adaptedReports}
        systemDocs={adaptedSystemDocs.filter((d) => d.page_type !== "deep_dive_candidates")}
        drafts={adaptedDrafts}
        images={adaptedImages}
        phases={phases}
        isFiction={isFiction}
        openDoc={openDoc}
        onOpenDoc={showDoc}
        openGroup={openTrailGroup}
        onToggleGroup={setOpenTrailGroup}
        previewDraftId={previewDraftId}
        onSelectDraft={selectDraft}
        publishedLabel={
          p.status === "published" && p.publication_date
            ? formatDate(p.publication_date, dateFormat)
            : null
        }
      />
    </>
  );
}

// ---------- Run Pipeline row ----------

interface RunRowProps {
  pieceId: number;
  agencyclientId: number;
  runPhaseFromRow: string | null;
  runErrorFromRow: string | null;
}

// FEAT-009 — minimal shape we need from /agencyclients to decide whether
// to surface the Run button or the "Operator-triggered" pill.
interface AgencyclientLite {
  id: number;
  runMode?: "auto" | "manual";
  run_mode?: "auto" | "manual";
}

// FEAT-069 / S-335 — replaces the raw `run_error` text previously rendered
// verbatim (in red) at all 3 RunPipelineRow branches below. Shows a plain-
// language headline by default via describeRunErrorMessage(); the raw
// technical message is available on demand behind a details toggle, same
// disclosure pattern as SkillErrorPanel's expand/collapse just below.
function RunErrorInline({ raw }: { raw: string }) {
  const [showDetails, setShowDetails] = React.useState(false);
  const friendly = describeRunErrorMessage(raw);
  return (
    <span style={{ color: "var(--danger, #c33)" }}>
      · {friendly}{" "}
      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          color: "inherit",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {showDetails ? "hide details" : "details"}
      </button>
      {showDetails && <span className="mono"> — {raw}</span>}
    </span>
  );
}

function SkillErrorPanel({ events }: { events: PieceRunEvent[] }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!events.length) return null;

  // Deduplicate: group identical errors across pg-boss retries and internal retries.
  // Key = skill + error_code + first 120 chars of message (truncate to avoid key bloat on long messages).
  type Grouped = { ev: PieceRunEvent; count: number };
  const grouped: Grouped[] = [];
  const seen = new Map<string, Grouped>();
  for (const ev of events) {
    const k = `${ev.skill}|${ev.error_code ?? ""}|${ev.message.slice(0, 120)}`;
    const existing = seen.get(k);
    if (existing) {
      existing.count++;
    } else {
      const g: Grouped = { ev, count: 1 };
      seen.set(k, g);
      grouped.push(g);
    }
  }

  const PREVIEW = 3;
  const visible = expanded ? grouped : grouped.slice(0, PREVIEW);
  const hidden = grouped.length - PREVIEW;

  return (
    <div
      style={{
        margin: "4px 16px 8px",
        padding: "8px 12px",
        background: "var(--danger-surface, #fff4f4)",
        border: "1px solid var(--danger-rule, #fcc)",
        borderRadius: 4,
        fontSize: 11,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <span style={{ fontWeight: 600, color: "var(--danger, #c33)" }}>
        Pipeline errors
        {events.length !== grouped.length && (
          <span style={{ fontWeight: 400, color: "var(--muted-fg)", marginLeft: 6 }}>
            ({grouped.length} unique · {events.length} total occurrences)
          </span>
        )}
      </span>
      {visible.map(({ ev, count }) => (
        <div key={ev.id}>
          <span className="mono" style={{ fontWeight: 600 }}>{ev.skill}</span>
          {ev.error_code && (
            <span className="mono" style={{ color: "var(--muted-fg)", marginLeft: 6 }}>
              [{ev.error_code}]
            </span>
          )}
          {count > 1 && (
            <span
              style={{
                marginLeft: 6,
                background: "var(--danger-rule, #fcc)",
                color: "var(--danger, #c33)",
                borderRadius: 10,
                padding: "0 5px",
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              ×{count}
            </span>
          )}
          <span style={{ marginLeft: 8 }}>{ev.message}</span>
          {ev.details?.operatorHint != null && (
            <div style={{ color: "var(--muted-fg)", marginTop: 2 }}>
              Hint: {String(ev.details.operatorHint)}
            </div>
          )}
        </div>
      ))}
      {grouped.length > PREVIEW && (
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            alignSelf: "flex-start",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "var(--danger, #c33)",
            fontSize: 11,
            textDecoration: "underline",
          }}
        >
          {expanded ? "Show less" : `Show ${hidden} more…`}
        </button>
      )}
    </div>
  );
}

function RunPipelineRow({
  pieceId,
  agencyclientId,
  runPhaseFromRow,
  runErrorFromRow,
}: RunRowProps) {
  const api = useApi();
  const qc = useQueryClient();
  // FEAT-108 / S-438 — Run is Admin and Editor work (requireEditor on the API).
  const { canEdit: canRun } = useRole();

  // FEAT-009 / S-107 — fetch the agencyclient's run_mode. Shared cache key
  // with every other view that reads "/agencyclients", so no extra round-trip
  // in practice. Stale-while-revalidate is fine; run_mode rarely changes.
  const agencyclientsQ = useApiQuery<AgencyclientLite[]>(
    ["agencyclients"],
    "/agencyclients"
  );
  const activeAgencyclient = agencyclientsQ.data?.find(
    (a) => a.id === agencyclientId
  );
  const runMode =
    activeAgencyclient?.runMode ?? activeAgencyclient?.run_mode ?? "auto";
  const isManualMode = runMode === "manual";

  // Poll run-status while a run looks in-flight (per the piece row's
  // runPhase + the explicit /run-status endpoint). The poll stops once
  // phase reaches `done` or `error` to avoid hammering the API after
  // a terminal state.
  const isInitiallyRunning =
    runPhaseFromRow !== null &&
    runPhaseFromRow !== "done" &&
    runPhaseFromRow !== "error";

  const [polling, setPolling] = useState(isInitiallyRunning);

  useEffect(() => {
    setPolling(
      runPhaseFromRow !== null &&
        runPhaseFromRow !== "done" &&
        runPhaseFromRow !== "error"
    );
  }, [runPhaseFromRow]);

  const runStatus = useApiQuery<RunStatusResponse>(
    ["piece-run-status", pieceId],
    `/pieces/${pieceId}/run-status`,
    {
      enabled: polling,
      refetchInterval: polling ? 5000 : false,
    }
  );

  // When the polled phase reaches terminal, stop polling AND refresh
  // the parent piece query so the row's runPhase / runError fields
  // reflect the final state.
  useEffect(() => {
    if (runStatus.data && isTerminalPhase(runStatus.data.phase)) {
      setPolling(false);
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
    }
  }, [runStatus.data, qc, pieceId]);

  const trigger = useMutation({
    mutationFn: async (): Promise<RunEnqueueResponse> => {
      return api<RunEnqueueResponse>(`/pieces/${pieceId}/run`, {
        method: "POST",
      });
    },
    onSuccess: () => {
      setPolling(true);
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
      void qc.invalidateQueries({
        queryKey: ["piece-run-status", pieceId],
      });
    },
  });

  // Friendly error message for the known pg-boss-broken state.
  const errorMessage = trigger.error ? describeRunError(trigger.error) : null;

  const phaseNow =
    runStatus.data?.phase ?? runPhaseFromRow ?? (trigger.isPending ? "queueing" : null);
  const runErr = visibleRunError(phaseNow, runStatus.data?.error ?? runErrorFromRow);

  const isErrorState = phaseNow === "error";
  const pieceEvents = useApiQuery<{ data: PieceRunEvent[] }>(
    ["piece-events", pieceId],
    `/pieces/${pieceId}/events`,
    { enabled: isErrorState }
  );
  // FEAT-009 / S-107 — manual-mode workspaces hide the Run Pipeline button
  // entirely (the API also refuses with 409 run_mode_manual, so the SPA
  // hide is a UX optimization, not the only enforcement). Pill is visible
  // to all roles for transparency.
  const skillEvents = pieceEvents.data?.data ?? [];

  if (isManualMode) {
    return (
      <>
      <div
        className="run-pipeline-row run-pipeline-row--operator"
        style={{
          padding: "8px 16px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: "var(--muted-fg)",
        }}
      >
        <span className="pill pill-muted">Operator-triggered</span>
        <span>
          Pipeline runs from the operator's CLI. Contact the operator to
          trigger this piece.
        </span>
        {phaseNow && (
          <span>
            · phase: <span className="mono">{phaseNow}</span>
          </span>
        )}
        {runErr && <RunErrorInline raw={runErr} />}
      </div>
      <SkillErrorPanel events={skillEvents} />
      </>
    );
  }

  if (!canRun) {
    // Reviewers still see the run state so they understand pipeline
    // progress, but cannot trigger it.
    if (!phaseNow && !runErr && !skillEvents.length) return null;
    return (
      <>
      <div
        className="run-pipeline-row"
        style={{
          padding: "8px 16px",
          fontSize: 12,
          color: "var(--muted-fg)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>Pipeline phase:</span>
        <span className="mono">{phaseNow ?? "idle"}</span>
        {runErr && <RunErrorInline raw={runErr} />}
      </div>
      <SkillErrorPanel events={skillEvents} />
      </>
    );
  }

  const isRunning =
    polling ||
    trigger.isPending ||
    (phaseNow !== null && !isTerminalPhase(phaseNow));

  return (
  <>
    <div
      className="run-pipeline-row"
      style={{
        padding: "8px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        borderTop: "1px solid var(--rule)",
      }}
    >
      <button
        type="button"
        className="btn-dec btn-approve"
        onClick={() => trigger.mutate()}
        disabled={isRunning}
        title="Enqueue a pipeline run for this piece"
      >
        <Icon name="flow" size={14} />{" "}
        {trigger.isPending
          ? "Queuing…"
          : isRunning
            ? "Running…"
            : "Run pipeline"}
      </button>
      <div
        style={{
          fontSize: 12,
          color: "var(--muted-fg)",
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <span>Status:</span>
        <span className="mono">{phaseNow ?? "idle"}</span>
        {polling && <span>· polling…</span>}
        {runErr && <RunErrorInline raw={runErr} />}
      </div>
      {errorMessage && (
        <div
          className="login-error"
          style={{ marginLeft: "auto", fontSize: 12 }}
        >
          {errorMessage}
        </div>
      )}
    </div>
    <SkillErrorPanel events={skillEvents} />
  </>
  );
}

// ---------- FEAT-015 — Spanish variant row + override picker ----------

interface SpanishVariantEditProps {
  pieceId: number;
  /** Per-piece override (null = inherit publication default). */
  override: string | null;
  /** The publication's default spanish_country (null = not set). */
  publicationDefault: string | null;
}

/**
 * Shows the effective Spanish variant for a piece with a source label, and an
 * inline picker that PATCHes the per-piece override. Rendered only for pieces
 * whose publication is Spanish-language (the caller gates on default_language).
 *
 * Effective resolution mirrors the orchestrator: override → publication
 * default → "Not set". Selecting "Use publication default" clears the override
 * (sends null). On success the piece query is invalidated so the row updates.
 */
function SpanishVariantEdit({
  pieceId,
  override,
  publicationDefault,
}: SpanishVariantEditProps) {
  const api = useApi();
  const { canEdit } = useRole();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: async (code: string | null) => {
      return api(`/pieces/${pieceId}`, {
        method: "PATCH",
        body: JSON.stringify({ spanish_country_override: code }),
      });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
      void qc.invalidateQueries({ queryKey: ["pieces"] });
    },
  });

  const effective = override ?? publicationDefault ?? null;
  const sourceLabel = override ? "(overridden)" : "(from publication)";

  if (editing) {
    return (
      <span className="meta-spanish-variant">
        <select
          className="settings-select"
          style={{ fontSize: 12, padding: "1px 4px" }}
          defaultValue={override ?? ""}
          disabled={mutation.isPending}
          onChange={(e) =>
            mutation.mutate(e.target.value === "" ? null : e.target.value)
          }
        >
          <option value="">Use publication default</option>
          {SPANISH_COUNTRY_OPTIONS.map((opt) => (
            <option key={opt.code} value={opt.code}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="meta-link-btn"
          onClick={() => setEditing(false)}
          disabled={mutation.isPending}
          style={{
            marginLeft: 6,
            fontSize: 12,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "inherit",
            textDecoration: "underline",
          }}
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <span className="meta-spanish-variant">
      <span>Spanish variant: {spanishVariantLabel(effective)}</span>{" "}
      <span style={{ opacity: 0.6 }}>{sourceLabel}</span>{" "}
      {canEdit && (
      <button
        type="button"
        className="meta-link-btn"
        onClick={() => setEditing(true)}
        style={{
          fontSize: 12,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "inherit",
          textDecoration: "underline",
        }}
      >
        Change
      </button>
      )}
    </span>
  );
}

// ---------- FEAT-011 / S-181 — Category display label (free text) ----------

interface CategoryEditProps {
  pieceId: number;
  /** Current editor-set category label (null = unset). */
  category: string | null;
}

/**
 * Free-text "Category" field in the piece metadata row. PATCHes
 * /pieces/:id with { category } (empty input sends null to clear) and
 * invalidates the piece query so the row updates. Mirrors the
 * SpanishVariantEdit inline-edit pattern.
 */
function CategoryEdit({ pieceId, category }: CategoryEditProps) {
  const { canEdit } = useRole();
  const api = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(category ?? "");

  const mutation = useMutation({
    mutationFn: async (next: string | null) => {
      return api(`/pieces/${pieceId}`, {
        method: "PATCH",
        body: JSON.stringify({ category: next }),
      });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
      void qc.invalidateQueries({ queryKey: ["pieces"] });
    },
  });

  function save() {
    const trimmed = value.trim();
    mutation.mutate(trimmed === "" ? null : trimmed);
  }

  if (editing) {
    return (
      <span className="meta-category">
        <input
          type="text"
          className="settings-input"
          style={{ fontSize: 12, padding: "1px 4px", width: 160 }}
          value={value}
          placeholder="e.g. Marketing Strategy"
          disabled={mutation.isPending}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setValue(category ?? "");
              setEditing(false);
            }
          }}
        />
        <button
          type="button"
          className="meta-link-btn"
          onClick={save}
          disabled={mutation.isPending}
          style={{
            marginLeft: 6,
            fontSize: 12,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "inherit",
            textDecoration: "underline",
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="meta-link-btn"
          onClick={() => {
            setValue(category ?? "");
            setEditing(false);
          }}
          disabled={mutation.isPending}
          style={{
            marginLeft: 6,
            fontSize: 12,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "inherit",
            textDecoration: "underline",
          }}
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <span className="meta-category">
      <span>Category: {category ? category : "—"}</span>{" "}
      {canEdit && (
      <button
        type="button"
        className="meta-link-btn"
        onClick={() => {
          setValue(category ?? "");
          setEditing(true);
        }}
        style={{
          fontSize: 12,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "inherit",
          textDecoration: "underline",
        }}
      >
        {category ? "Change" : "Set"}
      </button>
      )}
    </span>
  );
}
